type BaseViewportState = {
  screenLessThanLg: boolean;
  screenLargerThanSm: boolean;
  screenLargerOrEqualsLg: boolean;
  screenLargerOrEqualsMd: boolean;
  screenIsMd: boolean;
  screenIsSm: boolean;
};

type ClientViewportState = {
  [K in keyof BaseViewportState]: boolean | undefined;
};

type SsrSafeViewportState = {
  [K in keyof BaseViewportState]: ComputedRef<boolean>;
};

export const useSsrSafeViewportState = (): SsrSafeViewportState => {
  const { width } = useWindowSize({ initialWidth: 0, listenOrientation: true });
  const { screenPixelValues } = useTailwindBreakpoints();

  const { isMobile, isTablet, isDesktop, isMobileOrTablet, isDesktopOrTablet } =
    useDevice();

  const enableClientSideWidth = ref(false);
  const clientWidths: Ref<ClientViewportState> = ref({
    screenLessThanLg: undefined,
    screenLargerThanSm: undefined,
    screenLargerOrEqualsLg: undefined,
    screenLargerOrEqualsMd: undefined,
    screenIsMd: undefined,
    screenIsSm: undefined,
  });

  const updateClientWidthSizeState = ([w, enable]: [number, boolean]) => {
    if (!enable) {
      return;
    }

    clientWidths.value = {
      screenLessThanLg: w < screenPixelValues.value.lg,
      screenLargerThanSm: w > screenPixelValues.value.sm,
      screenLargerOrEqualsLg: w >= screenPixelValues.value.lg,
      screenLargerOrEqualsMd: w >= screenPixelValues.value.md,
      screenIsMd:
        w >= screenPixelValues.value.md && w < screenPixelValues.value.lg,
      screenIsSm: w < screenPixelValues.value.md,
    };
  };

  const debouncedUpdateClientWidthSizeState = useDebounceFn(
    updateClientWidthSizeState,
    15,
  );

  watch([width, enableClientSideWidth], debouncedUpdateClientWidthSizeState, {
    immediate: false,
  });

  onMounted(async () => {
    if (import.meta.client) {
      await nextTick();
      enableClientSideWidth.value = true;
    }
  });

  return {
    screenLessThanLg: computed(
      () => clientWidths.value.screenLessThanLg ?? isMobileOrTablet,
    ),
    screenLargerThanSm: computed(
      () => clientWidths.value.screenLargerThanSm ?? isDesktopOrTablet,
    ),
    screenLargerOrEqualsLg: computed(
      () => clientWidths.value.screenLargerOrEqualsLg ?? isDesktop,
    ),
    screenLargerOrEqualsMd: computed(
      () => clientWidths.value.screenLargerOrEqualsMd ?? isDesktopOrTablet,
    ),
    screenIsMd: computed(() => clientWidths.value.screenIsMd ?? isTablet),
    screenIsSm: computed(() => clientWidths.value.screenIsSm ?? isMobile),
  };
};
